import { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import {
  Upload,
  Download,
  Delete,
  AttachFile,
  Image,
  VideoLibrary,
  Description,
  Visibility,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import {
  useGetEvidenceQuery,
  useGetEvidenceDeadlineStatusQuery,
  useUploadEvidenceMutation,
  useDeleteEvidenceMutation,
  useLazyDownloadEvidenceQuery,
  usePublicizeCaseEvidenceMutation,
  useGetEvidencePoolQuery,
  useCreateEvidenceFromPoolMutation,
  useLazyGetEvidenceAiSuggestionsQuery,
  type EvidencePoolItem,
} from '../store/api/evidenceApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { hasPermission } from '../utils/permissions'
import { formatCaseDisplay } from '../utils/caseDisplay'
import { PageHeader } from '../components/PageHeader'
import SubmissionDeadlineCountdown from '../components/SubmissionDeadlineCountdown'
import { getFilterPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'
import { gradientBrand } from '../theme/brand'
import { getApiErrorMessage } from '../utils/apiErrorMessage'

export default function EvidencePage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [selectedCaseId, setSelectedCaseId] = useState<string>('')
  const [uploadForm, setUploadForm] = useState({
    case_id: '',
    team_id: '' as string,
    title: '',
    description: '',
    category: '',
    file: null as File | null,
  })

  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [], isLoading: isLoadingTeams } = useGetTeamsQuery()
  const { data: evidence = [], isLoading, refetch } = useGetEvidenceQuery(
    { case_id: selectedCaseId || undefined },
    { skip: false }
  )

  const { data: poolItems = [], isLoading: poolLoading } = useGetEvidencePoolQuery(selectedCaseId, {
    skip: !selectedCaseId,
  })
  const [createFromPool, { isLoading: isAddingFromPool }] = useCreateEvidenceFromPoolMutation()

  const skipAiSuggestions =
    !selectedCaseId ||
    currentUser?.role === 'student' ||
    currentUser?.role === 'coach'

  const [aiKeyword, setAiKeyword] = useState('')
  const [
    fetchAiSuggestions,
    {
      data: aiSuggestions,
      isFetching: aiLoading,
      isError: aiSuggestionsError,
      error: aiSuggestionsErr,
    },
  ] = useLazyGetEvidenceAiSuggestionsQuery()

  const [poolDropHover, setPoolDropHover] = useState(false)

  useEffect(() => {
    setAiKeyword('')
  }, [selectedCaseId])

  useEffect(() => {
    if (skipAiSuggestions || !selectedCaseId) return
    fetchAiSuggestions({ case_id: selectedCaseId })
  }, [selectedCaseId, skipAiSuggestions, fetchAiSuggestions])

  const [uploadEvidence, { isLoading: isUploading }] = useUploadEvidenceMutation()
  const [deleteEvidence] = useDeleteEvidenceMutation()
  const [downloadEvidence] = useLazyDownloadEvidenceQuery()
  const [publicizeCase, { isLoading: isPublicizing }] = usePublicizeCaseEvidenceMutation()
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })
  
  // View evidence dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [viewingEvidence, setViewingEvidence] = useState<any>(null)

  // Get user's team
  const userTeam = teams.find(
    (t) => t.members.includes(currentUser?.id || '') || t.coach_id === currentUser?.id
  )

  // Filter cases assigned to user's team
  const userCases = cases.filter((c) => {
    if (!userTeam) return false
    return c.assigned_team_ids?.includes(userTeam.id)
  })

  const caseIdForEvidenceDeadline = selectedCaseId || userCases[0]?.id || ''
  const { data: evidenceDeadlineStatus } = useGetEvidenceDeadlineStatusQuery(caseIdForEvidenceDeadline, {
    skip: !caseIdForEvidenceDeadline,
    pollingInterval: 1000,
  })
  const evidenceSubmissionOpen =
    !evidenceDeadlineStatus || evidenceDeadlineStatus.submission_open

  const { data: uploadDialogDeadline } = useGetEvidenceDeadlineStatusQuery(uploadForm.case_id, {
    skip: !uploadDialogOpen || !uploadForm.case_id,
    pollingInterval: uploadDialogOpen && uploadForm.case_id ? 1000 : undefined,
  })
  const uploadDialogSubmissionOpen =
    !uploadDialogDeadline || uploadDialogDeadline.submission_open

  const canUploadEvidence = currentUser && hasPermission(currentUser.role, 'UPLOAD_EVIDENCE')
  const canPublicizeEvidence =
    currentUser &&
    (hasPermission(currentUser.role, 'ASSIGN_CASES') ||
      hasPermission(currentUser.role, 'MANAGE_USERS'))

  const EVIDENCE_CATEGORIES = [
    'Hợp đồng',
    'Email/Tin nhắn',
    'Ảnh chụp',
    'Video',
    'Audio',
    'Biên bản',
    'Giấy tờ pháp lý',
    'Báo cáo',
    'Khác',
  ]

  const handleOpenUploadDialog = () => {
    if (canUploadEvidence && userTeam && !evidenceSubmissionOpen) {
      setSnackbar({
        open: true,
        message: evidenceDeadlineStatus?.message || 'Hết hạn nộp chứng cứ.',
        severity: 'error',
      })
      return
    }
    if (userCases.length > 0) {
      setUploadForm({
        ...uploadForm,
        case_id: userCases[0].id,
        team_id: userTeam?.id || '',
      })
    }
    setUploadDialogOpen(true)
  }

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false)
    setUploadForm({
      case_id: '',
      team_id: userTeam?.id || '',
      title: '',
      description: '',
      category: '',
      file: null,
    })
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setUploadForm({ ...uploadForm, file: event.target.files[0] })
    }
  }

  const handleUpload = async () => {
    setSnackbar({ ...snackbar, open: false })

    if (canUploadEvidence && !uploadDialogSubmissionOpen) {
      setSnackbar({
        open: true,
        message: uploadDialogDeadline?.message || 'Hết hạn nộp chứng cứ.',
        severity: 'error',
      })
      return
    }

    if (!uploadForm.file || !uploadForm.title || !uploadForm.case_id || !uploadForm.team_id) {
      setSnackbar({
        open: true,
        message: 'Vui lòng điền đầy đủ thông tin',
        severity: 'error',
      })
      return
    }

    const selectedCase = cases.find((c) => c.id === uploadForm.case_id)

    // Auto-generate tags: team, case, case_number (if available)
    const autoTags: string[] = []
    if (uploadForm.team_id) {
      const team = teams.find((t) => t.id === uploadForm.team_id)
      if (team) {
        autoTags.push(`team:${team.name}`)
      }
    }
    if (uploadForm.case_id) {
      if (selectedCase) {
        autoTags.push(`case:${formatCaseDisplay(selectedCase)}`)
      }
      if (selectedCase?.case_number) {
        autoTags.push(`case_number:${selectedCase.case_number}`)
      }
    }
    // Round sẽ được thêm sau nếu có field round trong case

    const formData = new FormData()
    formData.append('file', uploadForm.file)
    formData.append('case_id', uploadForm.case_id)
    formData.append('team_id', uploadForm.team_id)
    formData.append('title', uploadForm.title)
    if (uploadForm.description) formData.append('description', uploadForm.description)
    if (uploadForm.category) formData.append('category', uploadForm.category)
    // Append auto-generated tags
    autoTags.forEach((tag) => formData.append('tags[]', tag))

    try {
      await uploadEvidence(formData).unwrap()
      handleCloseUploadDialog()
      // RTK Query sẽ tự động refetch nhờ invalidatesTags
      // Nhưng để đảm bảo, gọi refetch thủ công với delay nhỏ
      setTimeout(() => {
        refetch()
      }, 500)
      setSnackbar({
        open: true,
        message: 'Upload chứng cứ thành công!',
        severity: 'success',
      })
    } catch (error: unknown) {
      setSnackbar({
        open: true,
        message: `Upload thất bại: ${getApiErrorMessage(error)}`,
        severity: 'error',
      })
    }
  }

  const handleDelete = async (evidenceId: string) => {
    if (!confirm('Bạn có chắc muốn xóa chứng cứ này?')) return

    try {
      await deleteEvidence(evidenceId).unwrap()
      // RTK Query sẽ tự động refetch nhờ invalidatesTags
      // Nhưng để đảm bảo, gọi refetch thủ công
      setTimeout(() => {
        refetch()
      }, 500)
      setSnackbar({
        open: true,
        message: 'Xóa thành công!',
        severity: 'success',
      })
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Xóa thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }
  
  const handleDownload = async (evidenceId: string, fileName: string) => {
    try {
      const result = await downloadEvidence(evidenceId).unwrap()
      const url = window.URL.createObjectURL(result)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setSnackbar({
        open: true,
        message: 'Download thành công!',
        severity: 'success',
      })
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Download thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }
  
  const handleViewEvidence = (item: any) => {
    setViewingEvidence(item)
    setViewDialogOpen(true)
  }
  
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false)
    setViewingEvidence(null)
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <Image />
    if (mimeType.startsWith('video/')) return <VideoLibrary />
    return <Description />
  }

  const handleAddFromPool = async (doc: EvidencePoolItem) => {
    if (!userTeam || !selectedCaseId) return
    if (canUploadEvidence && !evidenceSubmissionOpen) {
      setSnackbar({
        open: true,
        message: evidenceDeadlineStatus?.message || 'Hết hạn nộp chứng cứ.',
        severity: 'error',
      })
      return
    }
    try {
      await createFromPool({
        case_id: selectedCaseId,
        team_id: userTeam.id,
        document_id: doc.document_id,
        title: doc.title,
      }).unwrap()
      refetch()
      setSnackbar({
        open: true,
        message: 'Đã thêm chứng cứ từ pool tài liệu đề.',
        severity: 'success',
      })
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.data?.detail || error.message || 'Thêm từ pool thất bại',
        severity: 'error',
      })
    }
  }

  const handlePoolDragStart = (e: React.DragEvent, row: EvidencePoolItem) => {
    e.dataTransfer.setData('application/ecourt-pool-item', JSON.stringify(row))
    e.dataTransfer.effectAllowed = 'copy'
  }

  const parseDroppedPoolItem = (e: React.DragEvent): EvidencePoolItem | null => {
    try {
      const raw = e.dataTransfer.getData('application/ecourt-pool-item')
      if (!raw) return null
      const o = JSON.parse(raw) as EvidencePoolItem
      if (!o.document_id || o.case_id !== selectedCaseId) return null
      return o
    } catch {
      return null
    }
  }

  const handlePoolDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setPoolDropHover(false)
    const row = parseDroppedPoolItem(e)
    if (!row || !canUploadEvidence || !userTeam || !evidenceSubmissionOpen) return
    await handleAddFromPool(row)
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title="Quản lý Chứng cứ"
        subtitle="Tải lên chứng cứ theo đội, theo dõi hạn nộp và công khai hóa khi được phép."
        icon={<AttachFile />}
        actions={
          canUploadEvidence && userTeam ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Upload />}
              onClick={handleOpenUploadDialog}
              disabled={userCases.length === 0 || !evidenceSubmissionOpen}
              sx={{
                background: gradientBrand,
                '&:hover': { background: gradientBrand, filter: 'brightness(1.06)' },
              }}
            >
              Upload Chứng cứ
            </Button>
          ) : null
        }
      />

      {!canUploadEvidence && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Chỉ sinh viên (students) mới có quyền upload chứng cứ cho case được assign cho đội của mình.
        </Alert>
      )}

      {canUploadEvidence && userCases.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Bạn chưa có case nào được assign cho đội của mình.
        </Alert>
      )}

      {/* Filter — dùng danh sách case theo API (đã lọc theo vai trò) */}
      <Paper
        elevation={0}
        sx={(theme) => ({
          ...getFilterPaperSx(theme),
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { sm: 'center' },
        })}
      >
        <FormControl fullWidth sx={{ flex: 1 }}>
          <InputLabel>Lọc theo cuộc thi</InputLabel>
          <Select
            value={selectedCaseId}
            label="Lọc theo cuộc thi"
            onChange={(e) => setSelectedCaseId(e.target.value)}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {(cases.length ? cases : userCases).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {formatCaseDisplay(c)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {canPublicizeEvidence && (
          <Button
            variant="outlined"
            color="secondary"
            disabled={!selectedCaseId || isPublicizing}
            onClick={async () => {
              if (!selectedCaseId) return
              try {
                const res = await publicizeCase({ case_id: selectedCaseId }).unwrap()
                setSnackbar({
                  open: true,
                  message: `Đã công khai ${res.updated} chứng cứ cho vụ này.`,
                  severity: 'success',
                })
                refetch()
              } catch (error: any) {
                setSnackbar({
                  open: true,
                  message: error.data?.detail || error.message || 'Lỗi công khai',
                  severity: 'error',
                })
              }
            }}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Công khai chứng cứ (vụ đang chọn)
          </Button>
        )}
      </Paper>

      {canUploadEvidence &&
        userTeam &&
        evidenceDeadlineStatus &&
        !evidenceDeadlineStatus.submission_open && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Hết hạn nộp bài
            </Typography>
            <Typography variant="body2">{evidenceDeadlineStatus.message}</Typography>
          </Alert>
        )}

      {canUploadEvidence &&
        userTeam &&
        evidenceDeadlineStatus &&
        evidenceDeadlineStatus.submission_open &&
        Boolean(caseIdForEvidenceDeadline) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {evidenceDeadlineStatus.message}
            <SubmissionDeadlineCountdown status={evidenceDeadlineStatus} />
          </Alert>
        )}

      {selectedCaseId && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Pool tài liệu đề / vụ án
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Chọn nút Thêm hoặc kéo thả hàng tài liệu vào vùng bên phải để tạo bản chứng cứ đội (bản sao file).
            Sinh viên có quyền upload chứng cứ mới dùng kéo-thả / thêm.
          </Typography>
          {poolLoading ? (
            <CircularProgress size={28} />
          ) : poolItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Không có tài liệu pool cho cuộc thi này (cần tài liệu loại đề / vụ án do BTC đăng).
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                alignItems: 'stretch',
              }}
            >
              <TableContainer sx={{ flex: 1, minWidth: 0 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tiêu đề</TableCell>
                      <TableCell>Loại</TableCell>
                      <TableCell>File</TableCell>
                      <TableCell align="right">Thao tác</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {poolItems.map((row) => (
                      <TableRow
                        key={row.document_id}
                        hover
                        draggable={Boolean(
                          canUploadEvidence && userTeam && evidenceSubmissionOpen
                        )}
                        onDragStart={(e) => handlePoolDragStart(e, row)}
                        sx={{
                          cursor:
                            canUploadEvidence && userTeam && evidenceSubmissionOpen
                              ? 'grab'
                              : 'default',
                        }}
                      >
                        <TableCell>{row.title}</TableCell>
                        <TableCell>{row.document_type}</TableCell>
                        <TableCell>{row.file_name}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={
                              !canUploadEvidence ||
                              !userTeam ||
                              isAddingFromPool ||
                              !evidenceSubmissionOpen
                            }
                            onClick={() => handleAddFromPool(row)}
                          >
                            Thêm vào chứng cứ đội
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {canUploadEvidence && userTeam && (
                <Paper
                  variant="outlined"
                  onDragOver={(e) => {
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'copy'
                    setPoolDropHover(true)
                  }}
                  onDragLeave={() => setPoolDropHover(false)}
                  onDrop={handlePoolDrop}
                  sx={{
                    minHeight: 140,
                    width: { xs: '100%', md: 280 },
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: poolDropHover ? 'primary.main' : 'divider',
                    bgcolor: poolDropHover ? 'action.hover' : 'transparent',
                  }}
                >
                  <Typography align="center" variant="body2" color="text.secondary">
                    Kéo thả một dòng từ bảng pool vào đây để thêm vào chứng cứ đội.
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </Paper>
      )}

      {selectedCaseId && !skipAiSuggestions && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Gợi ý từ tài liệu vụ (RAG — BTC / Giám khảo)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Đội thi không thấy khối này và không gọi API. Gợi ý chỉ hỗ trợ tra cứu, không thay thế quyết định đội.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Từ khóa / câu hỏi"
              value={aiKeyword}
              onChange={(e) => setAiKeyword(e.target.value)}
              sx={{ minWidth: 200, flex: 1 }}
            />
            <Button
              variant="outlined"
              disabled={aiLoading}
              onClick={() =>
                fetchAiSuggestions({
                  case_id: selectedCaseId,
                  ...(aiKeyword.trim() ? { q: aiKeyword.trim() } : {}),
                })
              }
            >
              Tìm gợi ý
            </Button>
          </Box>
          {aiSuggestionsError ? (
            <Alert severity="warning">
              {(aiSuggestionsErr as { data?: { detail?: string } })?.data?.detail ||
                'Không tải được gợi ý (kiểm tra phạm vi phân công).'}
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {aiSuggestions?.notice}
              </Alert>
              {aiLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {(aiSuggestions?.suggestions || []).map((s) => (
                <Paper key={s.id} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {s.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {s.detail}
                  </Typography>
                  {s.source_document_id ? (
                    <Chip size="small" variant="outlined" label={`document_id: ${s.source_document_id}`} />
                  ) : null}
                </Paper>
              ))}
            </>
          )}
        </Paper>
      )}

      {/* Evidence Table */}
      {isLoading || isLoadingTeams ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : evidence.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Chưa có chứng cứ nào
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={(theme) => getTablePaperSx(theme)}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Tiêu đề</strong></TableCell>
                <TableCell><strong>Cuộc thi</strong></TableCell>
                <TableCell><strong>Đội thi</strong></TableCell>
                <TableCell><strong>Danh mục</strong></TableCell>
                <TableCell><strong>Kích thước</strong></TableCell>
                <TableCell><strong>Loại file</strong></TableCell>
                <TableCell><strong>Ngày upload</strong></TableCell>
                <TableCell><strong>Hiển thị</strong></TableCell>
                <TableCell align="right"><strong>Thao tác</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {evidence.map((item) => {
                const caseItem = cases.find((c) => c.id === item.case_id)
                const team = teams.find((t) => t.id === item.team_id)
                // Đảm bảo hiển thị tên đội, nếu không tìm thấy thì hiển thị ID
                const teamName = team ? team.name : item.team_id
                return (
                  <TableRow 
                    key={item.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'action.hover',
                        cursor: 'pointer'
                      } 
                    }}
                    onClick={() => handleViewEvidence(item)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getFileIcon(item.mime_type)}
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {item.title}
                          </Typography>
                          {item.description && (
                            <Tooltip title={item.description}>
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ 
                                  display: 'block',
                                  maxWidth: 300,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {item.description.length > 80 
                                  ? `${item.description.substring(0, 80)}...` 
                                  : item.description}
                              </Typography>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {caseItem ? formatCaseDisplay(caseItem) : item.case_id}
                    </TableCell>
                    <TableCell>
                      {teamName}
                    </TableCell>
                    <TableCell>
                      {item.category ? (
                        <Chip label={item.category} size="small" color="primary" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {(item.file_size / 1024 / 1024).toFixed(2)} MB
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.mime_type.split('/')[1] || item.mime_type} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {item.uploaded_at
                        ? new Date(item.uploaded_at).toLocaleDateString('vi-VN', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })
                        : '-'}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {item.is_public ? (
                        <Chip label="Công khai" size="small" color="success" variant="outlined" />
                      ) : (
                        <Chip label="Riêng đội" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Download />}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDownload(item.id, item.file_name)
                          }}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'white',
                            }
                          }}
                        >
                          Download
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleViewEvidence(item)
                          }}
                        >
                          Xem
                        </Button>
                        {canUploadEvidence && userTeam?.id === item.team_id && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDelete(item.id)
                            }}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="md" fullWidth>
        <DialogTitle>Upload Chứng cứ</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Cuộc thi *</InputLabel>
              <Select
                value={uploadForm.case_id}
                label="Cuộc thi *"
                onChange={(e) => setUploadForm({ ...uploadForm, case_id: e.target.value })}
                required
              >
                {userCases.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {formatCaseDisplay(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Tiêu đề *"
              value={uploadForm.title}
              onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Mô tả"
              value={uploadForm.description}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>Danh mục</InputLabel>
              <Select
                value={uploadForm.category}
                label="Danh mục"
                onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
              >
                <MenuItem value="">Không chọn</MenuItem>
                {EVIDENCE_CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Alert severity="info">
              Tags sẽ được tự động tạo theo: Đội thi, Cuộc thi, Vòng thi
            </Alert>
            <Button variant="outlined" component="label" fullWidth startIcon={<Upload />}>
              Chọn File
              <input
                type="file"
                hidden
                onChange={handleFileChange}
                accept=".pdf,.docx,.jpg,.jpeg,.png,.mp4,.mp3"
              />
            </Button>
            {uploadForm.file && (
              <Typography variant="body2">
                Đã chọn: {uploadForm.file.name} ({(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
            {uploadDialogDeadline && (
              <Alert severity={uploadDialogDeadline.submission_open ? 'info' : 'error'}>
                {uploadDialogDeadline.message}
                <SubmissionDeadlineCountdown status={uploadDialogDeadline} />
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>Hủy</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={isUploading || !uploadDialogSubmissionOpen}
          >
            {isUploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Evidence Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {viewingEvidence && getFileIcon(viewingEvidence.mime_type)}
            <Typography variant="h6">Chi tiết Chứng cứ</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingEvidence && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Tiêu đề
                </Typography>
                <Typography variant="body1">{viewingEvidence.title}</Typography>
              </Box>
              {viewingEvidence.description && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Mô tả
                  </Typography>
                  <Typography variant="body1">{viewingEvidence.description}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Cuộc thi
                </Typography>
                <Typography variant="body1">
                  {cases.find((c) => c.id === viewingEvidence.case_id) 
                    ? formatCaseDisplay(cases.find((c) => c.id === viewingEvidence.case_id)!)
                    : viewingEvidence.case_id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Đội thi
                </Typography>
                <Typography variant="body1">
                  {(() => {
                    const team = teams.find((t) => t.id === viewingEvidence.team_id)
                    return team ? team.name : (isLoadingTeams ? 'Đang tải...' : viewingEvidence.team_id)
                  })()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  File
                </Typography>
                <Typography variant="body1">{viewingEvidence.file_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Kích thước: {(viewingEvidence.file_size / 1024 / 1024).toFixed(2)} MB • {viewingEvidence.mime_type}
                </Typography>
              </Box>
              {viewingEvidence.category && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Danh mục
                  </Typography>
                  <Chip label={viewingEvidence.category} size="small" color="primary" />
                </Box>
              )}
              {viewingEvidence.tags && viewingEvidence.tags.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {viewingEvidence.tags
                      .filter((tag: string) => {
                        // Bỏ qua team_id và case_id nếu đã có team: và case:
                        if (tag.startsWith('team_id:') && viewingEvidence.tags.some((t: string) => t.startsWith('team:'))) {
                          return false
                        }
                        if (tag.startsWith('case_id:') && viewingEvidence.tags.some((t: string) => t.startsWith('case:'))) {
                          return false
                        }
                        return true
                      })
                      .map((tag: string, idx: number) => {
                        // Format tag để hiển thị tên thay vì ID
                        let displayTag = tag
                        if (tag.startsWith('team:')) {
                          displayTag = tag.replace('team:', 'Đội: ')
                        } else if (tag.startsWith('case:')) {
                          displayTag = tag.replace('case:', 'Cuộc thi: ')
                        } else if (tag.startsWith('case_id:')) {
                          const caseId = tag.replace('case_id:', '')
                          const caseItem = cases.find((c) => c.id === caseId)
                          displayTag = caseItem ? `Cuộc thi: ${formatCaseDisplay(caseItem)}` : tag
                        } else if (tag.startsWith('team_id:')) {
                          const teamId = tag.replace('team_id:', '')
                          const team = teams.find((t) => t.id === teamId)
                          displayTag = team ? `Đội: ${team.name}` : tag
                        } else if (tag.startsWith('case_number:')) {
                          displayTag = tag.replace('case_number:', 'Số vụ án: ')
                        }
                        return (
                          <Chip key={idx} label={displayTag} size="small" variant="outlined" />
                        )
                      })}
                  </Box>
                </Box>
              )}
              {viewingEvidence.uploaded_at && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Ngày upload
                  </Typography>
                  <Typography variant="body1">
                    {new Date(viewingEvidence.uploaded_at).toLocaleString('vi-VN')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {viewingEvidence && (
            <Button
              startIcon={<Download />}
              onClick={() => handleDownload(viewingEvidence.id, viewingEvidence.file_name)}
            >
              Download
            </Button>
          )}
          <Button onClick={handleCloseViewDialog}>Đóng</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
